import Cookies from 'js-cookie';
import { useQuery } from '@tanstack/react-query';
import { isBuilderAccountOwner } from '../../components/ai/aiVerticalScope';
import {
  getUserById,
  getAllUsers,
  getAllUsersByProjectId,
  getAllDesignations
} from '../../services/api/implementation/impl';

export function isManagementVertical(user) {
  return String(user?.vertical || '').toLowerCase() === 'management';
}

function userDisplayName(user) {
  return user?.name || user?.userName || '';
}

/** Map designation id -> name, so a user carrying only the FK still gets a label. */
function designationNameById(designations = []) {
  const map = new Map();
  for (const row of designations || []) {
    if (row?.id != null) map.set(String(row.id), row.name);
  }
  return map;
}

/**
 * Bracket text for a management user: their designation. The list payload gives
 * either the nested object or the `desgignation_id` FK (that spelling is the
 * API's), so resolve both; vertical is the last resort.
 */
function designationLabel(user, byId) {
  const nested = user?.designation?.name;
  if (nested) return nested;
  const id = user?.desgignation_id ?? user?.designation_id;
  const resolved = id != null ? byId?.get(String(id)) : null;
  return resolved || user?.vertical || 'Management';
}

function optionFromUser(user, byId) {
  return {
    label: `${userDisplayName(user)} (${designationLabel(user, byId)})`,
    value: user.id
  };
}

/**
 * Owner picker: Management users on this project + the company primary builder.
 *
 * `options.designations` resolves the designation shown in brackets.
 * Callers pass an already project-scoped user list (GET /users?project_ids=),
 * which is what limits the management users to the task's project.
 */
export function ownerUserOptions(users = [], currentUser = null, options = {}) {
  const byId = designationNameById(options?.designations);
  const result = [];
  const seen = new Set();

  for (const user of users) {
    if (!user?.id || !isManagementVertical(user) || seen.has(user.id)) continue;
    seen.add(user.id);
    result.push(optionFromUser(user, byId));
  }

  // GET /users excludes builder.primary_user_id, so the logged-in primary is
  // never in `users` (project-scoped or not) and must be added back here. They
  // own the account rather than sitting on a project, hence "Builder".
  if (isBuilderAccountOwner()) {
    const id = Number(currentUser?.id || Cookies.get('userId'));
    if (id && !seen.has(id)) {
      const name = userDisplayName(currentUser) || Cookies.get('userName') || '';
      result.unshift({
        label: `${name || 'Primary builder'} (Builder)`,
        value: id
      });
    }
  }

  return result;
}

/**
 * Owner options for `projectId`. Without a project (task not scoped to one yet)
 * it falls back to every management user.
 */
export function useOwnerUserOptions(projectId = null) {
  const userId = Cookies.get('userId');
  const { data: currentUser } = useQuery({
    queryKey: ['userLite', userId],
    queryFn: () => getUserById(userId),
    enabled: !!userId,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false
  });

  const { data: projectUsers } = useQuery({
    queryKey: ['projectUsers', projectId],
    queryFn: () => getAllUsersByProjectId(projectId, 1, 500),
    enabled: !!projectId
  });

  const { data: allUsers } = useQuery({
    queryKey: ['allUsers'],
    queryFn: () => getAllUsers(1, 1000),
    enabled: !projectId
  });

  const { data: designations } = useQuery({
    queryKey: ['designations'],
    queryFn: getAllDesignations,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false
  });

  const users = (projectId ? projectUsers?.data : allUsers?.data) || [];
  return ownerUserOptions(users, currentUser, { designations });
}
